/**
 * Generic Tiptap markdown editor over a playground file. Pattern adapted
 * from packages/app/src/components/MarkdownEditor.tsx minus the Bear-specific
 * bits (SVG code block extension, toolbar dropdowns, markdown-mode toggle,
 * link popover).
 *
 * Sync model:
 * - The file is opened through `playground.open(path)`, like MonacoEditor, so
 *   every write stamps the inode (`mtime`/`size`) the rest of the FS derives
 *   from. A `TextFile` is editable; anything else (mounted/managed content)
 *   is decoded once and rendered read-only.
 * - The editor instance is created once and is never destroyed for a
 *   `path` change: the open effect swaps in the new file, and the
 *   content-sync effect detaches the old file's listener, swaps the editor's
 *   text to the new file's, then listens for future changes (peers, agents).
 * - On editor updates, the editor's markdown is serialized and written
 *   back through `TextFile.change` with `updateText` for splice-level CRDT
 *   merging.
 *
 * Wiki-link sugar:
 * - The consumer supplies a `wikiLinks` URL adapter (`toHref` /
 *   `parseHref`) — usually a real route URL like `/files/<path>`. On load,
 *   `[[Title]]` in storage is inflated to `[Title](<toHref(Title)>)` so the
 *   link extension picks it up; on save the inverse transform restores the
 *   bracket form. Painting `data-wiki-*` attributes onto rendered anchors
 *   and intercepting clicks lives entirely in the `WikiLink` ProseMirror
 *   plugin; this file just pipes runtime props (`filePaths`,
 *   `onOpenWikiLink`) into the extension's storage.
 */
import { updateText } from "@automerge/automerge";
import type { Editor, JSONContent } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import {
  getHierarchicalIndexes,
  TableOfContents,
  type TableOfContentData,
} from "@tiptap/extension-table-of-contents";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import { Markdown } from "@tiptap/markdown";
import type { EditorView } from "@tiptap/pm/view";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ListTree } from "lucide-react";
import type { TextFile } from "playground/fs/types";
import type { Playground } from "playground/hal/index";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { invariant } from "ts-invariant";

import { COMMENT_CARD_WIDTH } from "../comments/CommentThread";
import { EditorCodeBlock, type CodeBlockPreviews } from "./code-block";
import {
  CommentHighlights,
  commentMarkRange,
  setCommentMarks,
} from "./comment-highlights";
import { EditorToolbar, type MarkdownCommentSelection } from "./EditorToolbar";
import { FootnoteDefinition, FootnoteReference } from "./footnote";
import { Frontmatter, splitFrontmatter } from "./frontmatter";
import { InlineFormatBubble } from "./InlineFormatBubble";
import { MarkdownClipboard } from "./markdown-clipboard";
import { BlockMathMarkdown, InlineMathMarkdown } from "./math";
import { TableMove } from "./table-move";
import { TableOfContentsPanel } from "./TableOfContentsPanel";
import {
  deflateWikiLinks,
  inflateWikiLinks,
  resolveWikiTarget,
  WIKI_LINK_FILES_META,
  WikiLink,
  type WikiUrlAdapter,
} from "./wiki-link";

/** What the open file gives the editor: a live text doc, or static text. */
type Source =
  | { kind: "text"; path: string; file: TextFile }
  | { kind: "static"; path: string; text: string };

// The extension defaults to random UUID anchor ids, which change on every
// reload and can't be linked to. Slugifying the heading text instead makes the
// id stable across sessions and peers, so `#my-heading` in a shared URL keeps
// resolving to the same heading. Collisions (two identical headings) share a
// slug — the browser scrolls to the first, matching how anchor links behave
// elsewhere.
function slugifyHeading(text: string): string {
  const slug = text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "section";
}

/** The outline as the panel draws it: same entries, same active heading. */
function sameOutline(a: TableOfContentData, b: TableOfContentData): boolean {
  return (
    a.length === b.length &&
    a.every(
      (item, i) =>
        item.id === b[i]?.id &&
        item.pos === b[i]?.pos &&
        item.level === b[i]?.level &&
        item.isActive === b[i]?.isActive &&
        item.textContent === b[i]?.textContent,
    )
  );
}

/** `CSS.escape` guarded for non-DOM environments (tests). */
function escapeId(id: string): string {
  return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id;
}

/** Pull image files out of a clipboard / dataTransfer FileList. Returns
 *  an empty array on null/undefined so the caller can early-out. */
function collectImageFiles(list: FileList | null | undefined): File[] {
  if (!list || list.length === 0) return [];
  return Array.from(list).filter((file) => file.type.startsWith("image/"));
}

/** Insert one image node per file at `startPos`, resolving each file's
 *  `src` via the supplied `resolveSrc` (data URL by default, or an
 *  uploader that returns a remote URL). Files resolve asynchronously so
 *  transactions are dispatched in source order with the running insert
 *  position advanced past each inserted node. Between awaits the caller's
 *  `isStillActive` predicate is re-checked so a file switch (the editor is
 *  reused across URLs) aborts the remaining inserts before they corrupt
 *  the new document. A `resolveSrc` returning `null` (read error, skipped
 *  upload) drops just that file. If the editor is torn down mid-flight the
 *  dispatch is silently skipped. */
async function insertImages(
  view: EditorView,
  files: File[],
  startPos: number,
  isStillActive: () => boolean,
  resolveSrc: (file: File) => Promise<string | null>,
): Promise<void> {
  const imageType = view.state.schema.nodes.image;
  if (!imageType) return;
  let pos = startPos;
  for (const file of files) {
    const src = await resolveSrc(file);
    if (src === null) continue;
    if (view.isDestroyed) return;
    if (!isStillActive()) return;
    const alt = file.name || "image";
    const node = imageType.create({ src, alt });
    const tr = view.state.tr.insert(pos, node);
    view.dispatch(tr);
    pos += node.nodeSize;
  }
}

function readAsDataUrl(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

export interface MarkdownEditorProps {
  /** Playground that owns the file. */
  playground: Playground;
  /**
   * Workspace-relative path. A text file is live and editable; mounted or
   * managed content renders read-only, as rendered prose rather than source.
   */
  path: string;
  placeholder?: string;
  /** Container className. Defaults to a min-height + padding wrapper. */
  className?: string;
  /**
   * Pane-managed focus: `true` focuses the editor (again once the file turns
   * out to be editable), `false` blurs it. Omit to leave focus alone.
   */
  isFocused?: boolean;
  /**
   * Scroll to the heading with this text once the outline knows it. A new
   * object reveals again; the same object is inert, like a jump target.
   */
  revealHeading?: { text: string };
  /**
   * Identity attached to Automerge change metadata. When provided, every
   * persisted change carries `message: JSON.stringify({ author })` so the
   * activity feed can attribute writes back to a person or agent.
   */
  author?: string;
  /**
   * Every path in the project tree (e.g. `useTreeEntries`). Used to flag wiki-links pointing to
   * missing files (`data-wiki-missing="true"`) and to resolve `[[Title]]`
   * to a concrete path on click. Omitting it skips both: links render with
   * `wikiLinks.toHref(target)` verbatim and the click handler dispatches
   * the raw target.
   */
  filePaths?: readonly string[];
  /**
   * URL adapter that maps wiki targets to in-editor anchor hrefs (and
   * back). The DOM anchors carry these hrefs verbatim — typically real
   * route paths like `/files/<path>` — so right-click / middle-click /
   * hover preview behave like any other link, no custom protocol.
   *
   * Omitting it disables wiki-link parsing entirely; `[[Title]]` storage
   * survives as literal text in the editor.
   */
  wikiLinks?: WikiUrlAdapter;
  /**
   * Called when the user clicks a wiki-link. Receives the resolved file
   * path (with `.md` suffix added if needed) when the target exists in
   * `filePaths`, otherwise the raw target string so the consumer can decide
   * how to handle the broken link (create it, show a picker, etc.).
   */
  onOpenWikiLink?: (path: string, exists: boolean) => void;
  /**
   * Uploads a pasted/dropped image and resolves to a renderable URL to use
   * as the image node's `src` (e.g. an externally-hosted reference), or
   * `null` to skip that file. When provided, paste/drop image insertion
   * routes through this uploader instead of embedding the image as an
   * inline base64 `data:` URL — keeping large blobs out of the backing
   * document. When omitted, the editor falls back to the inline data-URL
   * behavior, so this package stays generic and other consumers are
   * unaffected.
   */
  onImageUpload?: (file: File) => Promise<string | null>;
  /** Called with the Markdown source range of a rendered selection. */
  onCommentSelection?: (selection: MarkdownCommentSelection) => void;
  /** Reveal a source anchored comment in the rendered document. */
  revealComment?: { source: string; range: { start: number; end: number } };
  /**
   * Render a table-of-contents sidebar tracking the document's headings.
   * The panel appears (on `md`+ viewports) only once the document has more
   * than one heading, so short notes aren't cluttered by a one-item outline.
   * Clicking an entry scrolls the editor to that heading.
   */
  showToc?: boolean;
  /**
   * Extra code-block previews keyed by fence language. Each renders in place
   * of the source behind the same toggle mermaid uses (mermaid is built in;
   * Bear registers an `svg` floorplan preview). Read once when the editor is
   * constructed, so pass a stable object and read anything live from a ref.
   */
  codeBlockPreviews?: CodeBlockPreviews;
  /** Comment threads to highlight, and the open one's card. */
  comments?: MarkdownComments;
}

export interface MarkdownComments {
  /** The stored source the thread ranges index into. */
  source: string;
  threads: {
    id: string;
    range: { start: number; end: number };
  }[];
  activeId: string | null;
  onActivate(id: string): void;
  /** The open thread's card, shown under its range. */
  card: ReactNode;
}

/**
 * The document the editor shows for stored Markdown: wiki-links inflated and
 * a leading frontmatter block lifted into its own node (which the Markdown
 * parser would mangle as a rule and a heading). Loading a file and mapping a
 * comment into it both go through here, so they always agree.
 */
export function markdownDoc(
  editor: Editor,
  raw: string,
  wikiLinks?: WikiUrlAdapter,
): JSONContent {
  invariant(editor.markdown, "The editor has no Markdown extension");
  const inflated = wikiLinks ? inflateWikiLinks(raw, wikiLinks.toHref) : raw;
  const { raw: frontmatter, body } = splitFrontmatter(inflated);
  const parsed = editor.markdown.parse(body);
  return frontmatter === null
    ? parsed
    : {
        ...parsed,
        content: [
          { type: "frontmatter", attrs: { raw: frontmatter } },
          ...(parsed.content ?? []),
        ],
      };
}

type SourceRange = { start: number; end: number };
type DocRange = { from: number; to: number };

/**
 * Where source ranges land in the document, all from one parse: each range's
 * ends are marked in the source, the marked source is parsed as a load
 * would, and each marker's text offset is its position. Exact or nothing:
 * unless the parse, markers removed, reads as the document does, no range is
 * placed, since a marker inside a construct (a link's URL) can reshape it.
 */
function mapSourceRanges(
  editor: Editor,
  source: string,
  ranges: SourceRange[],
  wikiLinks?: WikiUrlAdapter,
): (DocRange | null)[] {
  if (!editor.markdown || ranges.length === 0) return ranges.map(() => null);
  const nonce = crypto.randomUUID().replaceAll("-", "").toUpperCase();
  const ends = ranges
    .flatMap(({ start, end }, i) => [
      { at: start, marker: `CMK${nonce}S${i}Z` },
      { at: end, marker: `CMK${nonce}E${i}Z` },
    ])
    .sort((a, b) => a.at - b.at);
  let marked = "";
  let cursor = 0;
  for (const { at, marker } of ends) {
    marked += source.slice(cursor, at) + marker;
    cursor = at;
  }
  marked += source.slice(cursor);
  const parsed = editor.schema.nodeFromJSON(
    markdownDoc(editor, marked, wikiLinks),
  );
  const pattern = new RegExp(`CMK${nonce}([SE])(\\d+)Z`, "g");
  if (
    parsed.textContent.replace(pattern, "") !== editor.state.doc.textContent
  ) {
    return ranges.map(() => null);
  }
  const from: number[] = [];
  const to: number[] = [];
  // Every marker before a point is text the real document doesn't have.
  let removed = 0;
  parsed.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    for (const match of node.text.matchAll(pattern)) {
      const at = pos + (match.index ?? 0) - removed;
      (match[1] === "S" ? from : to)[Number(match[2])] = at;
      removed += match[0].length;
    }
  });
  return ranges.map((_, i) => {
    const a = from[i];
    const b = to[i];
    return a !== undefined &&
      b !== undefined &&
      b >= a &&
      b <= editor.state.doc.content.size
      ? { from: a, to: b }
      : null;
  });
}

/**
 * Many ranges in one parse; when that parse can't be exact, each range on its
 * own, so one awkward thread can't hide the others.
 */
export function commentSourceRanges(
  editor: Editor,
  source: string,
  ranges: SourceRange[],
  wikiLinks?: WikiUrlAdapter,
): (DocRange | null)[] {
  const together = mapSourceRanges(editor, source, ranges, wikiLinks);
  return together.every((range) => range === null) && ranges.length > 1
    ? ranges.map(
        (range) =>
          mapSourceRanges(editor, source, [range], wikiLinks)[0] ?? null,
      )
    : together;
}

export function commentSourceRange(
  editor: Editor,
  source: string,
  range: SourceRange,
  wikiLinks?: WikiUrlAdapter,
): DocRange | null {
  return mapSourceRanges(editor, source, [range], wikiLinks)[0] ?? null;
}

export function MarkdownEditor({
  playground,
  path,
  placeholder = "Write…",
  className,
  isFocused,
  revealHeading,
  author,
  filePaths,
  wikiLinks,
  onOpenWikiLink,
  onImageUpload,
  onCommentSelection,
  revealComment,
  showToc = false,
  codeBlockPreviews,
  comments,
}: MarkdownEditorProps) {
  // The editor is built once, so its comment click handler reads this.
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const [cardAt, setCardAt] = useState<{ top: number; left: number } | null>(
    null,
  );
  // Bumped on every `setContent`, which replaces the document and with it the
  // comment highlights, so they are drawn again over the loaded content.
  const [seeds, setSeeds] = useState(0);
  // The open file for `path`, or null while it opens. Carries its own path so
  // effects keyed on it never pair one file's content with another's path.
  const [source, setSource] = useState<Source | null>(null);
  // Read-only ⟺ there is nowhere to write, which is the same condition
  // `persist` bails on. That includes the beat while a file is still opening:
  // an editable editor there is a trap, since keystrokes would land in Tiptap,
  // `persist` would drop them, and the content-sync effect would then seed the
  // stored text over the top.
  const readOnly = source?.kind !== "text";

  // Heading outline for the optional TOC sidebar, refreshed by the
  // `TableOfContents` extension's `onUpdate` on every relevant transaction.
  // Empty (and the panel hidden) until the extension reports headings.
  const [tocItems, setTocItems] = useState<TableOfContentData>([]);
  // The element that actually scrolls the document. The TOC extension needs
  // it to compute which heading is `isActive` as the user scrolls; a function
  // is passed to the extension so it reads the ref lazily (the editor is
  // constructed once, before this ref is populated).
  const scrollRef = useRef<HTMLDivElement>(null);
  // Per-file scroll offset. `scrollRef` is one element reused across tabs, so
  // without this the outgoing tab's `scrollTop` leaks into the incoming one.
  const scrollByPathRef = useRef<Map<string, number>>(new Map());
  // Overlay open/closed (narrow panes only). Ignored when the TOC renders as
  // an inline sidebar — that switch is CSS-driven via a container query on the
  // pane (see the `@container` root and `@min-[…]` classes in the render), so
  // it tracks the pane's own width, not the viewport (the left sidebar and
  // right chat shrink the pane without changing the viewport).
  const [tocOpen, setTocOpen] = useState(false);
  // A heading id from the URL hash to jump to once headings exist. Captured on
  // mount and whenever the file changes; consumed (and cleared) by the
  // deep-link effect once the matching heading has been assigned its id.
  const pendingHashRef = useRef<string | null>(null);

  // Inflated form (`[Title](<wikiUrls.toHref(...)>)`) of the last text we
  // either applied to the editor or persisted from a user edit. Dedupes
  // both directions so a write doesn't echo back as a setContent that
  // resets the cursor.
  const lastSyncedRef = useRef<string | null>(null);
  // Guards `onUpdate` so programmatic `setContent` doesn't loop back into
  // `persist`.
  const programmaticUpdateRef = useRef(false);

  // The wiki-link click callback is invoked from the storage `onClick`
  // hook installed in the layout effect below. A ref keeps the latest
  // callback live without re-running that effect on every render.
  const onOpenWikiLinkRef = useRef(onOpenWikiLink);
  useEffect(() => {
    onOpenWikiLinkRef.current = onOpenWikiLink;
  }, [onOpenWikiLink]);

  // The image uploader is read from the editorProps paste/drop closures,
  // which are created once (the editor instance is stable). A ref keeps
  // the latest callback live without recreating the editor.
  const onImageUploadRef = useRef(onImageUpload);
  useEffect(() => {
    onImageUploadRef.current = onImageUpload;
  }, [onImageUpload]);

  // The editor instance is reused across path changes, so an async
  // FileReader started on file A can resolve after the user has navigated
  // to file B. Without a guard, `view.dispatch` would insert the image
  // into B's content and `onUpdate` would persist it to B's file. The
  // paste/drop handlers snapshot this ref's current value at entry and
  // re-check it before each dispatch; a mismatch aborts the pending inserts.
  const currentPathRef = useRef(path);
  useEffect(() => {
    currentPathRef.current = path;
  }, [path]);

  // Open the file through the playground, so its writes stamp the inode. Same
  // shape as MonacoEditor: a text file stays open for live sync; anything else
  // is read once and closed.
  useEffect(() => {
    let cancelled = false;
    let opened: TextFile | null = null;
    setSource(null);
    void playground.open(path).then(async (file) => {
      if (cancelled) {
        void file.close();
        return;
      }
      if (file.kind === "text") {
        opened = file;
        setSource({ kind: "text", path, file });
        return;
      }
      try {
        invariant(file.kind === "blob", `${path} is not a markdown file`);
        const text = new TextDecoder().decode(await file.read());
        if (!cancelled) setSource({ kind: "static", path, text });
      } finally {
        void file.close();
      }
    });
    return () => {
      cancelled = true;
      void opened?.close();
    };
  }, [playground, path]);

  const persist = useCallback(
    (next: string) => {
      if (source?.kind !== "text") return;
      if (next === lastSyncedRef.current) return;
      lastSyncedRef.current = next;
      const storageForm = wikiLinks
        ? deflateWikiLinks(next, wikiLinks.parseHref)
        : next;
      const message = author ? JSON.stringify({ author }) : undefined;
      void source.file.change(
        (draft) => {
          if (typeof draft.text !== "string") {
            draft.text = storageForm;
            return;
          }
          updateText(draft, ["text"], storageForm);
        },
        message ? { message } : undefined,
      );
    },
    [source, author, wikiLinks],
  );

  // Editor instance is stable for the component's lifetime: path changes
  // do NOT destroy or recreate it. Tiptap's `useEditor` wrapper always
  // invokes the most recent `onUpdate` closure, so `persist` here always
  // writes to the current file.
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          bulletList: { keepMarks: true },
          orderedList: { keepMarks: true },
          // Replaced by EditorCodeBlock below (mermaid preview, lazy syntax
          // highlighting, language selector). Disable the built-in one so
          // there's a single codeBlock node.
          codeBlock: false,
          link: {
            autolink: true,
            linkOnPaste: true,
            // Disable the built-in click handler because it indiscriminately
            // calls `window.open(href)` on *any* anchor click — including
            // wiki links, which we route through `onOpenWikiLink` instead.
            // We handle external link clicks ourselves in
            // `editorProps.handleClick` below and let the WikiLink plugin
            // handle wiki clicks.
            openOnClick: false,
            // Render external anchors with `target="_blank"` + safe `rel`
            // so right-click → Open in New Tab and middle-click work
            // without exposing `window.opener`.
            HTMLAttributes: {
              target: "_blank",
              rel: "noopener noreferrer nofollow",
            },
          },
        }),
        TableKit.configure({ table: { resizable: true } }),
        // Row / column reordering, which TableKit itself doesn't ship.
        TableMove,
        // Renders previewable fences (```mermaid, plus whatever the consumer
        // registers) as live output with a source toggle, and everything else
        // as a lazily syntax-highlighted block with a hover language selector.
        // Stored as an ordinary fenced code block, so it round-trips through
        // markdown.
        EditorCodeBlock.configure({ previews: codeBlockPreviews ?? {} }),
        TaskList,
        TaskItem.configure({ nested: true }),
        // `inline: true` puts images in the paragraph schema so authors
        // (and agents) can embed them mid-sentence. `allowBase64` keeps
        // `data:` URLs intact through paste/drop and round-trips, which
        // is how SVGs and pasted screenshots arrive — the extension's own
        // `renderMarkdown` serializes the image node back to `![alt](src)`.
        Image.configure({
          inline: true,
          allowBase64: true,
        }),
        Placeholder.configure({ placeholder }),
        // LaTeX math: `$…$` inline and `$$…$$` block, rendered with KaTeX and
        // round-tripped through markdown by the `math` extensions. Agents lean
        // on LaTeX heavily, so a stored `.md` full of `$E = mc^2$` reads as
        // typeset math instead of raw source. `throwOnError: false` renders a
        // malformed expression as red inline text rather than crashing the
        // whole editor render on one bad backslash.
        InlineMathMarkdown.configure({
          katexOptions: { throwOnError: false },
        }),
        BlockMathMarkdown.configure({
          katexOptions: { throwOnError: false },
        }),
        // Footnotes: `[^1]` in prose and `[^1]: …` definitions. Without these
        // the syntax has no tokenizer, so the serializer's delimiter escaping
        // rewrites a stored `[^1]` to `\[^1\]` on every save. See footnote.ts.
        FootnoteReference,
        FootnoteDefinition,
        // Tracks the document's headings and pushes the outline into
        // `tocItems` for the sidebar. `getHierarchicalIndexes` numbers
        // headings by nesting depth; `scrollParent` points the active-heading
        // tracker at the editor's own scroll container (read lazily via the
        // ref since the editor is built before the DOM exists). The plugin
        // also stamps `data-toc-id` onto each heading, which the panel uses to
        // scroll to a clicked entry. `setState` from `useState` is stable, so
        // closing over it in this once-built config is safe.
        CommentHighlights.configure({
          onActivate: (id) => commentsRef.current?.onActivate(id),
        }),
        TableOfContents.configure({
          getIndex: getHierarchicalIndexes,
          getId: slugifyHeading,
          scrollParent: () => scrollRef.current ?? window,
          // It reports on every transaction (a selection, a comment
          // highlight); only a changed outline re-renders the editor.
          onUpdate: (content) =>
            setTocItems((current) =>
              sameOutline(current, content) ? current : content,
            ),
        }),
        // Only register the WikiLink extension (input rule for [[Title]],
        // click interception, decoration painter) when the consumer wired
        // up a URL adapter. Without one, leave the editor wiki-agnostic so
        // stored `[[Title]]` text survives edits as literal characters.
        ...(wikiLinks ? [WikiLink.configure({ urls: wikiLinks })] : []),
        // Holds a leading `---`-fenced YAML block (lifted in `seedContent`)
        // as a single collapsible node. Inert on docs without frontmatter —
        // just an unused schema node, no effect on other consumers.
        Frontmatter,
        Markdown,
        // Copy puts markdown source on the clipboard; pasted plain text is
        // parsed as markdown. See markdown-clipboard.ts.
        MarkdownClipboard,
      ],
      editorProps: {
        attributes: {
          // Cap the reading column and center it so wide screens don't
          // strand the prose against the left edge. The toolbar mirrors
          // this 48rem column (see EditorToolbar) so its icons sit above
          // the text while its border still spans the full pane.
          // `prose-sm` is the same scale the chat shell renders markdown
          // at (ChatPane), so a document reads the same size in both.
          class:
            "prose prose-sm dark:prose-invert mx-auto w-full max-w-[48rem] p-3 outline-none min-h-[8rem] bg-background text-foreground",
        },
        // Open non-wiki anchors in a new tab on left-click. Returning
        // `true` preempts ProseMirror's default cursor-placement so the
        // click doesn't both navigate *and* move the caret. Wiki hrefs
        // short-circuit (`return false`) so the WikiLink plugin's own
        // click handler can route them through `onOpenWikiLink`.
        handleClick(_view, _pos, event) {
          if (event.button !== 0) return false;
          if (event.defaultPrevented) return false;
          const eventTarget = event.target;
          if (!(eventTarget instanceof Element)) return false;
          const anchor = eventTarget.closest("a[href]");
          if (!anchor) return false;
          const href = anchor.getAttribute("href");
          if (!href) return false;
          if (wikiLinks && wikiLinks.parseHref(href) !== null) return false;
          event.preventDefault();
          window.open(href, "_blank", "noopener,noreferrer");
          return true;
        },
        handlePaste(view, event) {
          const files = collectImageFiles(event.clipboardData?.files);
          if (files.length === 0) return false;
          event.preventDefault();
          const originPath = currentPathRef.current;
          // Read the uploader from the ref so the latest prop value is
          // used even though this closure is created once. Falling back
          // to the inline data-URL reader keeps the default behavior.
          const upload = onImageUploadRef.current;
          void insertImages(
            view,
            files,
            view.state.selection.from,
            () => currentPathRef.current === originPath,
            upload ?? readAsDataUrl,
          );
          return true;
        },
        handleDrop(view, event) {
          const files = collectImageFiles(event.dataTransfer?.files);
          if (files.length === 0) return false;
          event.preventDefault();
          const coords = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });
          const originPath = currentPathRef.current;
          const upload = onImageUploadRef.current;
          void insertImages(
            view,
            files,
            coords?.pos ?? view.state.selection.from,
            () => currentPathRef.current === originPath,
            upload ?? readAsDataUrl,
          );
          return true;
        },
      },
      content: "",
      onUpdate: ({ editor }) => {
        if (programmaticUpdateRef.current) return;
        persist(editor.getMarkdown());
      },
    },
    [],
  );

  // Scroll the editor to a heading by its stable id and, optionally, reflect it
  // in the URL hash so the location is copyable. `replaceState` keeps the
  // router's existing history-state object intact (only the hash changes), so
  // the SPA router doesn't desync and no history entry is pushed per click.
  const scrollToHeading = useCallback(
    (id: string, updateUrl: boolean) => {
      if (!editor || editor.isDestroyed) return;
      const target = editor.view.dom.querySelector(
        `[data-toc-id="${escapeId(id)}"]`,
      );
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      if (updateUrl && typeof window !== "undefined") {
        window.history.replaceState(window.history.state, "", `#${id}`);
      }
    },
    [editor],
  );

  // A programmatic reveal (the agent's `show` tool) is the hash mechanism
  // without the URL: wait until the outline has the slug, scroll once.
  const revealedRef = useRef<{ text: string } | null>(null);
  useEffect(() => {
    if (!revealHeading || revealedRef.current === revealHeading) return;
    const id = slugifyHeading(revealHeading.text);
    if (!tocItems.some((item) => item.id === id)) return;
    revealedRef.current = revealHeading;
    scrollToHeading(id, false);
  }, [revealHeading, tocItems, scrollToHeading]);

  // Capture the URL hash to honor as a deep-link target whenever the file
  // changes (or on first mount). The heading may not have its slug id yet —
  // the effect below waits for the outline to populate before scrolling.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.slice(1);
    pendingHashRef.current = hash ? decodeURIComponent(hash) : null;
  }, [path]);

  // Once the extension has assigned slug ids (tocItems populated), jump to the
  // pending hash target if one matches. Jump without smooth scrolling so a
  // deep-link lands immediately, and clear the pending hash so later edits
  // don't re-trigger it.
  useEffect(() => {
    const id = pendingHashRef.current;
    if (!id || !editor || editor.isDestroyed) return;
    if (!tocItems.some((item) => item.id === id)) return;
    const target = editor.view.dom.querySelector(
      `[data-toc-id="${escapeId(id)}"]`,
    );
    // Only consume the pending hash once we've actually scrolled — if the
    // outline reports the id before its DOM node is queryable, a later
    // `tocItems` update retries rather than silently dropping the deep-link.
    if (target) {
      pendingHashRef.current = null;
      target.scrollIntoView({ block: "start" });
    }
  }, [editor, tocItems]);

  // Toggle editability live: the editor instance is reused across files, so a
  // read-only mounted file followed by an editable doc (or vice versa) just
  // flips this — Tiptap keeps the content, only the write path changes.
  useEffect(() => {
    if (!editor) return;
    // `emitUpdate: false` is load-bearing. Tiptap's `setEditable` defaults to
    // emitting an `update` event, and this effect runs (on mount) *before* the
    // content-sync effect below has seeded the file's text — so the editor is
    // still holding its initial empty `content` and `programmaticUpdateRef` is
    // still false. A default `setEditable(true)` would fire `onUpdate` with
    // empty markdown and `persist("")` would wipe the doc (recoverable only via
    // history). Suppressing the event keeps editability a pure view toggle.
    editor.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  // Follow the pane's focus. Re-runs when editability flips, since a file
  // still opening is read-only and can't take the caret yet.
  useEffect(() => {
    if (!editor || editor.isDestroyed || isFocused === undefined) return;
    if (isFocused) editor.commands.focus();
    else editor.commands.blur();
  }, [editor, isFocused, readOnly]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || !revealComment || !editor.markdown)
      return;
    const range = commentSourceRange(
      editor,
      revealComment.source,
      revealComment.range,
      wikiLinks,
    );
    if (!range) return;
    editor.commands.setTextSelection(range);
    editor.commands.scrollIntoView();
  }, [editor, revealComment, wikiLinks]);

  // Map each thread's source range into the document, then place the open
  // thread's card under its highlight, in the scroll container's content box.
  // Mapping parses the whole file, so it runs when the text or the threads
  // change, not when a thread is merely opened.
  const threadsKey = comments
    ? JSON.stringify([comments.source, comments.threads])
    : "";
  const placed = useMemo(() => {
    const current = commentsRef.current;
    if (!editor || editor.isDestroyed || !editor.markdown || !current) {
      return [];
    }
    const ranges = commentSourceRanges(
      editor,
      current.source,
      current.threads.map((thread) => thread.range),
      wikiLinks,
    );
    return current.threads.flatMap((thread, i) => {
      const range = ranges[i];
      return range ? [{ id: thread.id, ...range }] : [];
    });
    // `commentsRef` is read for the threads `threadsKey` names; `seeds`
    // changes when the document under them is replaced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, threadsKey, seeds, wikiLinks]);
  const activeId = comments?.activeId ?? null;
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const marks = placed.map((mark) => ({
      ...mark,
      active: mark.id === activeId,
    }));
    setCommentMarks(editor.view, marks);
    const active = activeId ? commentMarkRange(editor.state, activeId) : null;
    const container = scrollRef.current;
    if (!active || !container) {
      setCardAt(null);
      return;
    }
    const box = container.getBoundingClientRect();
    const at = editor.view.coordsAtPos(active.from);
    setCardAt({
      top:
        editor.view.coordsAtPos(active.to).bottom -
        box.top +
        container.scrollTop +
        6,
      left: Math.max(
        8,
        Math.min(at.left - box.left, box.width - COMMENT_CARD_WIDTH - 16),
      ),
    });
  }, [editor, placed, activeId]);

  // Record the active file's offset live. Keyed on `editor`: the scroll
  // container only mounts once the editor exists, so `scrollRef` is null before.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      scrollByPathRef.current.set(currentPathRef.current, el.scrollTop);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [editor]);

  // Seed the editor with a markdown string: inflate wiki-links, lift a leading
  // frontmatter block into its node, and `setContent` without echoing back
  // through `onUpdate`. Shared by the file-driven (editable) sync below and
  // the static (read-only) path — same content pipeline, different source.
  const seedContent = useCallback(
    (raw: string) => {
      if (!editor || editor.isDestroyed) return;
      lastSyncedRef.current = wikiLinks
        ? inflateWikiLinks(raw, wikiLinks.toHref)
        : raw;
      programmaticUpdateRef.current = true;
      editor.commands.setContent(markdownDoc(editor, raw, wikiLinks), {
        emitUpdate: false,
      });
      programmaticUpdateRef.current = false;
      setSeeds((count) => count + 1);
    },
    [editor, wikiLinks],
  );

  // Read-only static content (mounted/managed `.md`). Seed once; no listener,
  // no persist. Re-seed on Tiptap's post-mount `create` for the same DOM-lag
  // race the editable path guards against.
  useEffect(() => {
    if (!editor || source?.kind !== "static") return;
    const { text } = source;
    seedContent(text);
    const reapply = () => seedContent(text);
    editor.on("create", reapply);
    return () => {
      editor.off("create", reapply);
    };
  }, [editor, source, seedContent]);

  // The single point at which the editor reacts to the open file. When a new
  // text file arrives this effect re-runs: detach the old file's listener,
  // sync the new file's text into the editor, then listen for future changes.
  useEffect(() => {
    if (!editor || source?.kind !== "text") return;
    const { file, path: sourcePath } = source;
    // `read()` resolves after this effect may have been torn down.
    let attached = true;

    // `firstApply` is per-mount, `lastSyncedRef` is per-component. We
    // bypass the persistent dedupe on the very first call of each mount
    // so the editor's content is *always* (re)seeded on attach — the
    // ref can carry a value from a previous mount's apply or from
    // `persist` (when the user typed before the editor was re-opened),
    // and skipping the initial setContent on that basis leaves the
    // editor blank on first open (the bug this guards against).
    // Subsequent in-mount applies (driven by `file.onChange`)
    // still dedupe so user keystrokes don't echo back.
    let firstApply = true;
    const apply = (raw: string) => {
      // Tiptap's `useEditor` constructor schedules a 1ms destroy timer
      // that's normally cleared by its own useEffect on mount, but on a
      // slow first open (lazy chunk + file opening + deep
      // tree) the timer can win the race and null `commandManager`
      // before the editor is ever destroyed from React's perspective.
      // Touching `editor.commands` on that destroyed instance throws
      // "Cannot read properties of null (reading 'commands')". Same
      // hazard applies to `file.onChange` firing after the editor
      // has been destroyed by a route transition.
      if (!attached || editor.isDestroyed) return;
      const next = wikiLinks ? inflateWikiLinks(raw, wikiLinks.toHref) : raw;
      if (!firstApply && next === lastSyncedRef.current) return;
      const isInitial = firstApply;
      firstApply = false;
      // Same content pipeline as the read-only path (frontmatter lift, wiki
      // inflate, no-echo setContent) — seedContent owns it.
      seedContent(raw);
      // Restore this file's offset only on the first seed (open/tab switch),
      // after seedContent so the content is tall enough — a later collaborative
      // change must not yank the user's scroll.
      if (isInitial) {
        const el = scrollRef.current;
        if (el) el.scrollTop = scrollByPathRef.current.get(sourcePath) ?? 0;
      }
    };
    // `read()` snapshots at call time but resolves later. An input rule writes
    // twice in one tick, so the first write's snapshot would land after
    // `lastSyncedRef` moved on and clobber the editor. Drop a superseded
    // snapshot: the newer change's own event delivers the latest text.
    const refresh = async () => {
      const text = await file.read();
      if (text === (await file.read())) apply(text);
    };

    void refresh();
    const unsubscribe = file.onChange(refresh);
    // Belt-and-suspenders: re-apply once Tiptap's post-mount
    // `setTimeout(0)` init fires `create`. The synchronous `apply()`
    // above already updates `editor.state`, but if the view's DOM
    // wasn't fully wired up yet (e.g. EditorContent hadn't migrated
    // `view.dom` out of the default detached div for some reason), the
    // rendered DOM can lag. Re-firing on `create` re-runs setContent
    // against the fully-initialised view. If `create` already fired,
    // the listener simply never runs — Tiptap emits it at most once
    // per editor.
    const reapply = () => {
      firstApply = true;
      void refresh();
    };
    editor.on("create", reapply);
    return () => {
      attached = false;
      unsubscribe();
      editor.off("create", reapply);
    };
  }, [editor, source, wikiLinks, seedContent]);

  // Push runtime knobs onto the WikiLink extension's storage:
  //   - `filePaths` is read by the decoration plugin to decide which links are
  //     missing, and by the click handler to resolve a target to a path.
  //   - `onClick` is read by the click plugin when the user activates a
  //     wiki-link anchor.
  // The decoration plugin re-runs naturally on every transaction; we
  // dispatch a no-op transaction with the `wikiLinkFiles` meta whenever
  // `filePaths` changes so the painted state stays in sync without waiting
  // for the user to type. Using `useLayoutEffect` so the refreshed
  // decorations are committed before the browser paints.
  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) return;
    // `wikiLink` storage is only present when the extension was wired in
    // (i.e. consumer supplied `wikiLinks`). With no adapter, there are no
    // wiki anchors to paint or intercept — skip.
    const storage = editor.storage.wikiLink;
    if (!storage) return;
    storage.paths = filePaths ?? null;
    storage.onClick = (target: string) => {
      const resolved = storage.paths
        ? resolveWikiTarget(target, storage.paths)
        : null;
      onOpenWikiLinkRef.current?.(resolved ?? target, resolved !== null);
    };
    editor.view.dispatch(editor.state.tr.setMeta(WIKI_LINK_FILES_META, true));
    return () => {
      storage.onClick = null;
    };
  }, [editor, filePaths]);

  // A TOC is worth showing only past a single heading. Both presentations —
  // the pinned inline sidebar and the slide-down overlay — are rendered when
  // `hasToc`; a container query on the pane shows exactly one (see the
  // `@min-[992px]:` classes). 992px is the pane width at which the 14rem
  // sidebar stops crowding the 48rem reading column (768 + 224); narrower than
  // that, the overlay floats over the content so the prose keeps full width.
  const hasToc = showToc && tocItems.length > 1;

  const selectHeading = useCallback(
    (id: string) => {
      scrollToHeading(id, true);
      setTocOpen(false);
    },
    [scrollToHeading],
  );

  return (
    // `@container` makes this pane a container-query context so the TOC's
    // inline-vs-overlay switch below tracks the pane width, not the viewport.
    <div className={`@container ${className ?? "h-full w-full flex flex-col"}`}>
      {/* No formatting toolbar for read-only content — nothing to format. The
          TOC toggle rides in the toolbar when editable; read-only panes get a
          floating toggle below. */}
      {editor && !readOnly ? (
        <EditorToolbar
          editor={editor}
          onCommentSelection={onCommentSelection}
          tocToggle={
            hasToc
              ? { open: tocOpen, onToggle: () => setTocOpen((v) => !v) }
              : undefined
          }
        />
      ) : null}
      {editor ? (
        <div className="relative flex flex-1 overflow-hidden">
          {/* This div — not EditorContent — is the scroll container the TOC
              extension tracks for the active heading. */}
          <div ref={scrollRef} className="relative flex-1 overflow-auto">
            <EditorContent editor={editor} />
            {comments?.card && cardAt ? (
              <div className="absolute z-30" style={cardAt}>
                {comments.card}
              </div>
            ) : null}
            {/* Inline formatting bar over text selections. Editable only —
                nothing to format in a read-only render. */}
            {!readOnly ? (
              <InlineFormatBubble
                editor={editor}
                onCommentSelection={onCommentSelection}
              />
            ) : null}
          </div>
          {hasToc ? (
            <>
              {/* Wide pane: pinned sidebar. Hidden below 992px. */}
              <TableOfContentsPanel
                variant="sidebar"
                className="hidden @min-[992px]:block"
                items={tocItems}
                onSelect={selectHeading}
              />
              {/* Narrow pane: slide-down overlay + a trigger. Read-only panes
                  have no toolbar to host the toggle, so float one over the
                  content; both hide at 992px where the sidebar takes over. */}
              {readOnly ? (
                <button
                  type="button"
                  title="Table of contents"
                  aria-label="Table of contents"
                  aria-pressed={tocOpen}
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => setTocOpen((v) => !v)}
                  className={`absolute right-2 top-2 z-30 rounded border border-border bg-background p-1.5 text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground @min-[992px]:hidden ${
                    tocOpen ? "bg-muted text-foreground" : ""
                  }`}
                >
                  <ListTree className="h-4 w-4" />
                </button>
              ) : null}
              <TableOfContentsPanel
                variant="overlay"
                open={tocOpen}
                className="@min-[992px]:hidden"
                items={tocItems}
                onSelect={selectHeading}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
